import { Send, MessageCircle, ChevronDown, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TelegramUser } from "@/types";
import { useState } from "react";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useTelegram } from "@/contexts/UnifiedAuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { useProfile } from "@/hooks/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import { base64Encode } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const TELEGRAM_BOT_URL = "https://t.me/voice2doc_bot";

interface TelegramProfilePhotoProps {
  photoUrl?: string;
}

const TelegramProfilePhoto = ({ photoUrl }: TelegramProfilePhotoProps) => {
  const { t } = useTranslation();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={t('profile.telegram.photo_alt')}
        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
      />
    );
  }

  return (
    <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
      <User size={32} className="text-blue-300" />
    </div>
  );
};

interface TelegramProfileDetailsProps {
  username: string;
  onCheckStatus: () => void;
  isConnected: boolean;
}

const TelegramProfileDetails = ({
  username,
  onCheckStatus,
  isConnected,
}: TelegramProfileDetailsProps) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium">Voice2doc Support</h3>
        <p className="text-sm text-muted-foreground font-mono">@{username}</p>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <MessageCircle size={16} />
        <span>{t('profile.telegram.connected_to_bot')}</span>
        <a
          href={TELEGRAM_BOT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline font-medium"
        >
          Voice2doc
        </a>
      </div>
      {!isConnected && (
        <Button
          variant="outline"
          size="sm"
          onClick={onCheckStatus}
          className="flex items-center gap-2"
        >
          <MessageCircle size={16} />
          {t('profile.telegram.check_status')}
        </Button>
      )}
    </div>
  );
};

interface TelegramSectionHeaderProps {
  isConnected: boolean;
  isExpanded: boolean;
  username?: string;
  onToggleExpand: () => void;
  onConnect: () => void;
}

const TelegramSectionHeader = ({
  isConnected,
  isExpanded,
  username,
  onToggleExpand,
  onConnect,
}: TelegramSectionHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
          <Send size={20} className="rotate-45 relative -translate-x-0.5" />
        </div>
        <div>
          <h2 className="text-xl font-medium">{t('profile.telegram.title')}</h2>
          {!isExpanded && (
            <p className="text-sm text-muted-foreground mt-1 font-mono">
              {username ? `@${username}` : t('profile.telegram.not_connected')}
            </p>
          )}
        </div>
      </div>
      {isConnected ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleExpand}
          className="h-10 w-10"
        >
          <ChevronDown
            size={20}
            className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </Button>
      ) : (
        <Button onClick={onConnect} size="sm" className="flex items-center gap-2">
          <MessageCircle size={16} />
          {t('profile.telegram.connect_btn')}
        </Button>
      )}
    </div>
  );
};

export const TelegramSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isTelegram, telegramUser } = useTelegram();
  const { addNotification } = useNotifications();
  const { profile } = useProfile(user?.id);
  const queryClient = useQueryClient();

  const [isExpanded, setIsExpanded] = useState(false);

  const handleConnectTelegram = () => {
    if (!user?.id) {
      addNotification({
        userId: user?.id || "",
        type: "error",
        message: t('profile.telegram.error_no_id_desc'),
      });
      return;
    }

    // Encode the parameters in base64
    const params = `telegram_connect&user_id=${user.id}`;
    const encodedParams = base64Encode(params);
    const telegramUrl = `${TELEGRAM_BOT_URL}?start=${encodedParams}`;
    window.open(telegramUrl, "_blank");

    addNotification({
      userId: user?.id || "",
      type: "info",
      message: t('profile.telegram.connect_info'),
    });
  };

  const handleCheckConnectionStatus = () => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      addNotification({
        userId: user.id,
        type: "info",
        message: t('profile.telegram.checking_status'),
      });
    }
  };

  const username = isTelegram
    ? telegramUser?.username
    : profile?.telegram_username;
  const isConnected = Boolean(profile?.telegram_id);

  return (
    <div className="bg-card border rounded-lg p-6">
      <TelegramSectionHeader
        isConnected={isConnected}
        isExpanded={isExpanded}
        username={username}
        onToggleExpand={() => setIsExpanded(!isExpanded)}
        onConnect={handleConnectTelegram}
      />

      {isConnected && isExpanded && (
        <div className="mt-6">
          <div className="flex items-center gap-6">
            {isTelegram && (
              <div className="relative">
                <TelegramProfilePhoto photoUrl={telegramUser?.photo_url} />
              </div>
            )}
            <TelegramProfileDetails
              username={username || ""}
              onCheckStatus={handleCheckConnectionStatus}
              isConnected={isConnected}
            />
          </div>
        </div>
      )}
    </div>
  );
};
